import { useEffect, useLayoutEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './stores/auth';
import { AppLayout } from './components/AppLayout';
import { RequirePermission } from './components/RequirePermission';
import { BrandLoader } from './components/BrandLoader';
import { useHoldWhileLoading, releaseBootLoader } from './lib/appReady';
import { clearTheme } from './lib/theme';
import LoginPage from './pages/Login';
import SignupPage from './pages/Signup';

// Public marketing site
import SitePage from './pages/site/Home';
import SiteListings from './pages/site/Listings';
import SiteListingDetail from './pages/site/ListingDetail';
import SiteViewing from './pages/site/Viewing';
import ApplicantPage from './pages/site/Applicant';
import ApplicantSignPage from './pages/site/ApplicantSign';
import SiteAbout from './pages/site/About';
import SiteContact from './pages/site/Contact';

// Authenticated app
import HomePage from './pages/Home';
import MyDayPage from './pages/MyDay';
import CalendarPage from './pages/Calendar';
import MessagesPage from './pages/Messages';
import OutboxPage from './pages/Outbox';
import BankingPage from './pages/Banking';
import ImportsPage from './pages/Imports';
import ImportJobPage from './pages/ImportJob';
import ArrearsPage from './pages/Arrears';
import AgentBillsPage from './pages/AgentBills';
import AgentDashboardPage from './pages/AgentDashboard';
import TemplatesPage from './pages/Templates';
import TaxRatesPage from './pages/TaxRates';
import ItemsPage from './pages/Items';
import IntegrationsPage from './pages/Integrations';
import PropertiesPage from './pages/Properties';
import PropertyDetailPage from './pages/PropertyDetail';
import UnitDetailPage from './pages/UnitDetail';
import LettingsRedirect, { LettingsApplicationsPage, LettingsAvailablePage, LettingsOffersPage, LettingsViewingsPage } from './pages/Lettings';
import MediaPage from './pages/Media';
import MarketingListingsPage from './pages/marketing/Listings';
import BuildingPage from './pages/Building';
import PropertyPage from './pages/Property';
import ListingDetailPage from './pages/ListingDetail';
import TenancyDetailPage from './pages/TenancyDetail';
import WorkOrderDetailPage from './pages/WorkOrderDetail';
import ContactsPage from './pages/Contacts';
import MoneyPage from './pages/Money';
import VendorDetailPage from './pages/VendorDetail';
import AssetDetailPage from './pages/AssetDetail';
import ContractorDocumentsPage from './pages/ContractorDocuments';
import BillsPage from './pages/Bills';
import BillDetailPage from './pages/BillDetail';
import PropertyFundsPage from './pages/PropertyFunds';
import InvoicesPage from './pages/Invoices';
import InvoiceDetailPage from './pages/InvoiceDetail';
import PaymentsPage from './pages/Payments';
import AccountsPage from './pages/Accounts';
import JournalsPage from './pages/Journals';
import PeriodsPage from './pages/Periods';
import StatementsPage from './pages/Statements';
import ReportsPage from './pages/Reports';
import UsersPage from './pages/Users';
import EntitiesPage from './pages/Entities';
import EntityDetailPage from './pages/EntityDetail';
import FeesPage from './pages/Fees';
import InvestorsPage from './pages/Investors';
import AuditPage from './pages/Audit';
import MyHomePage from './pages/resident/MyHome';
import MyBookingPage from './pages/applicant/MyBooking';
import ApplyFromViewingPage from './pages/applicant/ApplyFromViewing';
import PortalProfilePage from './pages/tenant/Profile';
import PortalNotificationsPage from './pages/tenant/Notifications';
import ChangePasswordPage from './pages/ChangePassword';

/** /app/dashboards/:id was the old detail route; it is now a view on the home screen. */

/** /rent/:slug/apply → /rent/:slug/viewing (the website only books a viewing now). */
function ApplyToViewing() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/rent/${slug}/viewing`} replace />;
}

function LegacyDashboardRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/v/${id}`} replace />;
}

/**
 * The retired list pages now live elsewhere: Assets, Contracts and Budgets on Building's sub-tabs, Work orders,
 * Compliance and Inspections on Property's, the eight lettings pages on the four Lettings pages, Deposits on Accounts'
 * (/app/money), and Vendors, Contacts and Residents on Contacts' (/app/contacts). Old links land there, keeping the query
 * keys that still mean something (?filter=, ?contact=, ?search=…).
 */
function Moved({ to, keep = [] }: { to: string; keep?: string[] }) {
  const [params] = useSearchParams();
  const [path, q] = to.split('?');
  const next = new URLSearchParams(q);
  for (const k of keep) { const v = params.get(k); if (v) next.set(k, v); }
  const qs = next.toString();
  return <Navigate to={qs ? `${path}?${qs}` : path!} replace />;
}

/** /app/vendors (the old "Vendors & contacts") → Contacts, on the sub-tab it was showing. */
function VendorsMoved() {
  const [params] = useSearchParams();
  return params.get('sub') === 'contacts'
    ? <Moved to="/app/contacts?sub=contacts" keep={['filter', 'contact']} />
    : <Moved to="/app/contacts" keep={['tab', 'search']} />;
}

/**
 * The signed-in app wears the admin look: an `admin` class on <html> re-points the design tokens (index.css
 * `html.admin`). The public site — sign-in, sign-up and the forced password change included — keeps its warm palette
 * and is always light.
 */
const ADMIN_PREFIXES = ['/app'];
function AdminScope() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const isAdmin = ADMIN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
    document.documentElement.classList.toggle('admin', isAdmin);
    // Leaving the app (signing out lands on /login): drop its theme; the public site is always light.
    if (!isAdmin) { clearTheme(); releaseBootLoader(true); }
  }, [pathname]);
  return null;
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  // The boot loader stays up while the session is restored (it covers this splash on a reload).
  useHoldWhileLoading(loading);
  if (loading) return <BrandLoader fullscreen size={72} />;
  if (!user) return <Navigate to="/login" replace />;
  // After an administrator's reset nothing else opens until a new password is chosen (Task R).
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

/** The forced change sits outside the app shell: signed in, but nothing else loads. */
function ForcedPasswordChange() {
  const { user, loading } = useAuth();
  useEffect(() => { if (!loading) releaseBootLoader(); }, [loading]);
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.mustChangePassword) return <Navigate to="/app/change-password" replace />;
  return <ChangePasswordPage />;
}

export default function App() {
  const restore = useAuth((s) => s.restore);
  useEffect(() => { void restore(); }, [restore]);

  return (
    <>
    <AdminScope />
    <Routes>
      {/* Public marketing website — no authentication */}
      <Route path="/" element={<SitePage />} />
      <Route path="/rent" element={<SiteListings />} />
      <Route path="/rent/:slug" element={<SiteListingDetail />} />
      {/* Apply left the website (2 Nov 2026): the application comes after the viewing, from their account. */}
      <Route path="/rent/:slug/apply" element={<ApplyToViewing />} />
      <Route path="/rent/:slug/viewing" element={<SiteViewing />} />
      {/* The applicant's own page, from the link an agent's referral emails them (no sign-in) */}
      <Route path="/apply/:token" element={<ApplicantPage />} />
      {/* The agreement's signing page alone — the app opens it in a WebView for a booking made in the app */}
      <Route path="/apply/:token/sign" element={<ApplicantSignPage />} />
      <Route path="/locations" element={<Navigate to="/rent" replace />} />
      <Route path="/about" element={<SiteAbout />} />
      <Route path="/contact" element={<SiteContact />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Resident sign-up with the invite code from the welcome letter (Task J) */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/:code" element={<SignupPage />} />
      <Route path="/change-password" element={<ForcedPasswordChange />} />

      {/* One authenticated application for every role */}
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        {/* One home screen: the built-in overview and every saved view live here. */}
        <Route index element={<HomePage />} />
        <Route path="overview" element={<HomePage overview />} />
        <Route path="v/:id" element={<RequirePermission page="dashboards"><HomePage /></RequirePermission>} />
        <Route path="change-password" element={<ChangePasswordPage />} />
        <Route path="my-home" element={<MyHomePage />} />
        <Route path="my-home/:section" element={<MyHomePage />} />
        <Route path="my-booking" element={<RequirePermission page="applicantPortal"><MyBookingPage /></RequirePermission>} />
        {/* Apply for the flat of a completed viewing (docs/plans/viewing-first-onboarding.md, phase 4), prefilled from it. */}
        <Route path="my-booking/apply/:leadId" element={<RequirePermission page="applicantPortal"><ApplyFromViewingPage /></RequirePermission>} />
        {/* The tenant portal's Profile and Notifications (a resident or an applicant; opened from the avatar and the bell). */}
        <Route path="profile" element={<RequirePermission page="residents" alsoPages={['applicantPortal']}><PortalProfilePage /></RequirePermission>} />
        <Route path="notifications" element={<RequirePermission page="residents" alsoPages={['applicantPortal']}><PortalNotificationsPage /></RequirePermission>} />
        {/* The Director page is now part of the home Dashboard; old links keep their panel / AI action. */}
        <Route path="director" element={<Moved to="/app" keep={['tab', 'ai']} />} />
        <Route path="portfolio" element={<Navigate to="/app/reports" replace />} />
        <Route path="my-day" element={<RequirePermission page="myDay"><MyDayPage /></RequirePermission>} />
        <Route path="calendar" element={<RequirePermission page="calendar"><CalendarPage /></RequirePermission>} />
        <Route path="messages" element={<RequirePermission page="messages"><MessagesPage /></RequirePermission>} />
        <Route path="banking" element={<RequirePermission page="banking"><BankingPage /></RequirePermission>} />
        <Route path="agent-bills" element={<RequirePermission page="agentBills"><AgentBillsPage /></RequirePermission>} />
        <Route path="agent" element={<RequirePermission page="reservations"><AgentDashboardPage /></RequirePermission>} />
        <Route path="arrears" element={<RequirePermission page="arrears"><ArrearsPage /></RequirePermission>} />
        <Route path="arrears/:tenancyId" element={<RequirePermission page="arrears"><ArrearsPage /></RequirePermission>} />
        <Route path="outbox" element={<RequirePermission page="outbox"><OutboxPage /></RequirePermission>} />
        <Route path="templates" element={<RequirePermission page="templates"><TemplatesPage /></RequirePermission>} />
        <Route path="tax-rates" element={<RequirePermission page="taxRates"><TaxRatesPage /></RequirePermission>} />
        <Route path="integrations" element={<RequirePermission page="integrations"><IntegrationsPage /></RequirePermission>} />

        {/* The dashboards used to be their own section; keep old links working. */}
        <Route path="dashboards" element={<Navigate to="/app" replace />} />
        <Route path="dashboards/:id" element={<LegacyDashboardRedirect />} />

        <Route path="properties" element={<PropertiesPage />} />
        <Route path="properties/:id" element={<RequirePermission page="properties"><PropertyDetailPage /></RequirePermission>} />
        <Route path="properties/:id/units/:unitId" element={<RequirePermission page="units"><UnitDetailPage /></RequirePermission>} />
        <Route path="units" element={<Navigate to="/app/properties" replace />} />
        {/* Lettings: four pages, each behind its own permission pages (the same lists as the sidebar's Lettings group and
            LETTINGS_PAGES in lib/lettings.ts — 3 Nov 2026). /app/lettings and its old ?sub= addresses redirect to them with
            their other params kept. Listings moved to Marketing › Listings (2026-10-26); Tenancies and Changeovers to
            Property (2026-10-22). */}
        <Route path="lettings" element={<LettingsRedirect />} />
        <Route path="lettings/viewings" element={<RequirePermission page="viewings" alsoPages={['leads']}><LettingsViewingsPage /></RequirePermission>} />
        <Route path="lettings/applications" element={<RequirePermission page="applications" alsoPages={['reservations']}><LettingsApplicationsPage /></RequirePermission>} />
        <Route path="lettings/available" element={<RequirePermission page="reservations" alsoPages={['listings']}><LettingsAvailablePage /></RequirePermission>} />
        <Route path="lettings/offers" element={<RequirePermission page="offers"><LettingsOffersPage /></RequirePermission>} />
        <Route path="marketing/media" element={<RequirePermission page="media"><MediaPage /></RequirePermission>} />
        <Route path="marketing/listings" element={<RequirePermission page="listings"><MarketingListingsPage /></RequirePermission>} />
        <Route path="marketing" element={<Navigate to="/app/marketing/media" replace />} />
        <Route path="listings" element={<Moved to="/app/marketing/listings" keep={['status', 'search']} />} />
        <Route path="listings/:id" element={<RequirePermission page="listings"><ListingDetailPage /></RequirePermission>} />
        <Route path="leads" element={<Moved to="/app/lettings/viewings?show=requests" keep={['lead', 'search']} />} />
        <Route path="viewings" element={<Moved to="/app/lettings/viewings?show=booked" keep={['feedback', 'search']} />} />
        <Route path="offers" element={<Moved to="/app/lettings/offers" />} />
        <Route path="applications" element={<Moved to="/app/lettings/applications" keep={['flat', 'filter']} />} />
        <Route path="tenancies" element={<Moved to="/app/property?sub=tenancies" keep={['filter', 'inArrears', 'search']} />} />
        <Route path="tenancies/:id" element={<RequirePermission page="tenancies"><TenancyDetailPage /></RequirePermission>} />
        <Route path="changeovers" element={<Moved to="/app/property?sub=changeovers" keep={['propertyId', 'phase', 'filter']} />} />
        <Route path="reservations" element={<Moved to="/app/lettings/applications" keep={['sheet', 'flat', 'propertyId', 'filter']} />} />
        <Route path="deposits" element={<Moved to="/app/money?sub=deposits" keep={['filter', 'search']} />} />
        <Route path="residents" element={<Moved to="/app/contacts?sub=residents" keep={['search']} />} />

        {/* Building and Property: each page gates its sub-tabs on their own permissions (Building: buildingRecord, assets,
            contracts, budgets; Property: workOrders, compliance, inspections, tenancies, changeovers). */}
        <Route path="building" element={<BuildingPage />} />
        <Route path="property" element={<PropertyPage />} />
        <Route path="work-orders" element={<Moved to="/app/property?sub=works" keep={['propertyId', 'buildingId', 'unitId', 'open', 'priority', 'status', 'slaBreached', 'filter', 'search']} />} />
        <Route path="work-orders/:id" element={<RequirePermission page="workOrders"><WorkOrderDetailPage /></RequirePermission>} />
        <Route path="assets" element={<Moved to="/app/building?sub=plant" keep={['bmf', 'search']} />} />
        <Route path="assets/:id" element={<RequirePermission page="assets"><AssetDetailPage /></RequirePermission>} />
        <Route path="compliance" element={<Moved to="/app/property?sub=certificates" keep={['status', 'certificateType', 'filter', 'search']} />} />
        {/* Contacts: the page gates each sub-tab on its own permission (vendors, contacts, residents). A vendor keeps its own
            page at /app/vendors/:id; /app/vendors itself redirects (?sub=contacts → the address book). */}
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="vendors" element={<VendorsMoved />} />
        <Route path="vendors/:id" element={<RequirePermission page="vendors"><VendorDetailPage /></RequirePermission>} />
        <Route path="my-documents" element={<RequirePermission page="contractorPortal"><ContractorDocumentsPage /></RequirePermission>} />
        <Route path="contracts" element={<Moved to="/app/building?sub=contracts" keep={['filter', 'contract', 'expiring']} />} />
        <Route path="inspections" element={<Moved to="/app/property?sub=visits" keep={['status', 'overdue', 'propertyId', 'filter']} />} />

        <Route path="bills" element={<RequirePermission page="bills"><BillsPage /></RequirePermission>} />
        <Route path="items" element={<RequirePermission page="items"><ItemsPage /></RequirePermission>} />
        <Route path="bills/new" element={<RequirePermission page="bills"><BillDetailPage /></RequirePermission>} />
        <Route path="bills/:id" element={<RequirePermission page="bills"><BillDetailPage /></RequirePermission>} />
        <Route path="property-funds" element={<RequirePermission page="propertyFunds"><PropertyFundsPage /></RequirePermission>} />
        <Route path="invoices" element={<RequirePermission page="invoices"><InvoicesPage /></RequirePermission>} />
        <Route path="invoices/new" element={<RequirePermission page="invoices"><InvoiceDetailPage /></RequirePermission>} />
        <Route path="invoices/:id" element={<RequirePermission page="invoices"><InvoiceDetailPage /></RequirePermission>} />
        <Route path="transactions" element={<RequirePermission page="payments"><PaymentsPage /></RequirePermission>} />
        <Route path="payments" element={<Navigate to="/app/transactions" replace />} />
        <Route path="accounts" element={<RequirePermission page="chartOfAccounts"><AccountsPage /></RequirePermission>} />
        {/* Managers → Accounts (the chart of accounts above keeps /app/accounts): the page gates its sub-tabs (fees or
            budgets for the overview, deposits). */}
        <Route path="money" element={<MoneyPage />} />
        <Route path="journals" element={<RequirePermission page="journals"><JournalsPage /></RequirePermission>} />
        <Route path="periods" element={<RequirePermission page="periods"><PeriodsPage /></RequirePermission>} />
        <Route path="statements" element={<RequirePermission page="glReports"><StatementsPage /></RequirePermission>} />

        <Route path="reports" element={<RequirePermission page="reports"><ReportsPage /></RequirePermission>} />

        <Route path="users" element={<RequirePermission page="users"><UsersPage /></RequirePermission>} />
        <Route path="roles" element={<Moved to="/app/users?roles=1" />} />
        <Route path="entities" element={<RequirePermission page="entities"><EntitiesPage /></RequirePermission>} />
        <Route path="entities/:id" element={<RequirePermission page="entities"><EntityDetailPage /></RequirePermission>} />
        <Route path="owners" element={<Navigate to="/app/entities" replace />} />
        <Route path="fees" element={<RequirePermission page="fees"><FeesPage /></RequirePermission>} />
        <Route path="imports" element={<RequirePermission page="imports"><ImportsPage /></RequirePermission>} />
        <Route path="imports/:id" element={<RequirePermission page="imports"><ImportJobPage /></RequirePermission>} />
        <Route path="budgets" element={<Moved to="/app/building?sub=budgets" />} />
        <Route path="investors" element={<RequirePermission page="investors"><InvestorsPage /></RequirePermission>} />
        <Route path="investors/:id" element={<RequirePermission page="investors"><InvestorsPage /></RequirePermission>} />
        <Route path="audit" element={<RequirePermission page="auditLog"><AuditPage /></RequirePermission>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
